// src/context/AuthProvider.jsx (or wherever you keep it)
import React, { createContext, useContext, useEffect, useState } from "react";
import axios from "axios";

const API_BASE = "https://landlord-portal-y3wv.onrender.com";
const REFRESH_URL = "/api/v1/auth/landlord/refresh-token";

const AuthCtx = createContext(null);

// ---------- helpers ----------
const getPersistStore = () =>
  localStorage.getItem("remember_me") === "true"
    ? localStorage
    : sessionStorage;

const getAny = (key) =>
  localStorage.getItem(key) ?? sessionStorage.getItem(key) ?? null;

const parseJSON = (raw) => {
  try {
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
};

const writeAuth = ({
  access_token,
  token_type = "Bearer",
  refresh_token,
  user,
  user_email,
}) => {
  const store = getPersistStore();
  if (access_token) store.setItem("access_token", access_token);
  if (token_type) store.setItem("token_type", token_type);
  if (refresh_token) store.setItem("refresh_token", refresh_token);
  if (user) store.setItem("user", JSON.stringify(user));
  if (user_email) store.setItem("user_email", user_email);
};

const clearAuthEverywhere = () => {
  const keys = [
    "access_token",
    "refresh_token",
    "token_type",
    "user",
    "user_email",
    "remember_me",
  ];
  [localStorage, sessionStorage].forEach((s) =>
    keys.forEach((k) => s.removeItem(k))
  );
};

// ---------- provider ----------
export function AuthProvider({ children }) {
  const [status, setStatus] = useState("loading"); // loading | authenticated | unauthenticated
  const [user, setUser] = useState(null);

  useEffect(() => {
    let alive = true;

    async function boot() {
      // read from *either* storage
      const access = getAny("access_token");
      const refresh = localStorage.getItem("refresh_token");
      const userRaw = getAny("user");
      const userObj = parseJSON(userRaw);

      if (access) {
        setUser(userObj);
        if (alive) setStatus("authenticated");
        return;
      }

      if (!refresh) {
        if (alive) setStatus("unauthenticated");
        return;
      }

      // silent refresh with raw axios (avoid interceptor loops)
      try {
        const res = await axios.post(`${API_BASE}${REFRESH_URL}`, {
          refresh_token: refresh,
        });

        const {
          access_token,
          token_type = "Bearer",
          refresh_token: newRefresh,
          data: serverUser, // in case backend returns user here
        } = res?.data || {};

        if (!access_token) throw new Error("No new access token");

        // write new tokens to the *persisting* store chosen by remember_me
        writeAuth({
          access_token,
          token_type,
          refresh_token: newRefresh || refresh,
          user: userObj || serverUser || null,
        });

        setUser(userObj || serverUser || null);
        if (alive) setStatus("authenticated");
      } catch (e) {
        clearAuthEverywhere();
        if (alive) setStatus("unauthenticated");
      }
    }

    boot();

    // cross-tab sync
    const onStorage = (e) => {
      if (
        !e.key ||
        ["access_token", "refresh_token", "user", "remember_me"].includes(e.key)
      ) {
        const hasAccessNow = !!getAny("access_token");
        setStatus(hasAccessNow ? "authenticated" : "unauthenticated");
        if (e.key === "user") setUser(parseJSON(getAny("user")));
      }
    };
    window.addEventListener("storage", onStorage);

    return () => {
      alive = false;
      window.removeEventListener("storage", onStorage);
    };
  }, []);

  const value = {
    status,
    user,
    logout: () => {
      clearAuthEverywhere();
      setUser(null);
      setStatus("unauthenticated");
    },
  };

  return <AuthCtx.Provider value={value}>{children}</AuthCtx.Provider>;
}

export const useAuth = () => useContext(AuthCtx);
